// Формации — situațiile de scalping, găsite automat.
//
// Fila răspunde la o singură întrebare: „unde se întâmplă ceva chiar acum?". De aceea
// nu arată tot ce a găsit scanerul, ci lucrurile cu punctaj mare, ordonate. Un tabel cu
// o sută de rânduri e la fel de inutil ca lipsa lui.
//
// Punctajul nu e o notă inventată: fiecare confirmare independentă adaugă puncte —
// închiderea dincolo de nivel raportată la ATR, volumul barei față de medie, câte
// atingeri avea nivelul, dacă volatilitatea chiar s-a extins. Le arătăm pe toate pe
// card, ca să poți verifica singur de ce o formație a primit nota pe care o are.
//
// Peste punctaj stă **nota** (A/B/C): ea adaugă ce nu se vede pe grafic — fluxul adunat
// de pe patru burse, interesul deschis, cât din stop mănâncă comisionul. Fiecare card
// are și **planul**: unde intri, unde e stopul, unde e ținta. Sus, jurnalul spune cât
// au adus de fapt semnalele de fiecare notă, în R după comision — nu ce promit.
//
// Graficul de pe card e același `Chart` ca în grilă — lumânări vii, care se mișcă odată
// cu piața, nu o poză. Două lucruri fac asta suportabil cu zeci de carduri deodată:
// graficul se construiește abia când cardul intră în ecran, iar la reîmprospătare nu
// reconstruim lista, ci doar diferența. O formație care rămâne pe ecran își păstrează
// graficul viu; se schimbă doar cifrele din jurul lui.

import { el, clear, fmtPrice, fmtVolume } from '../core/util.js';
import { renderPlan, renderFlow, drawPlanLines, miniLegend, coinButtons } from '../ui/setup-parts.js';
import { openSetupView } from '../ui/setup-view.js';
import { settings } from '../core/state.js';
import { tiger, tigerState } from '../core/tiger.js';
import { iconMoneda, iconBursa } from '../ui/coin-icon.js';
import { icon } from '../ui/icons.js';
import { baseTicker, tigerBtn, toast, dropdown } from '../ui/components.js';
import { Chart } from '../chart/chart.js';
import { chartTheme } from '../core/theme.js';

const FELURI = [
  { id: '', label: 'Все сетапы', icon: 'fa-border-all' },
  { id: 'breakout', label: 'Пробой', icon: 'fa-arrow-trend-up' },
  { id: 'false_breakout', label: 'Ложный пробой', icon: 'fa-shuffle' },
  { id: 'bounce', label: 'Отскок', icon: 'fa-arrow-turn-up' },
  { id: 'squeeze', label: 'Сжатие', icon: 'compress' },
  { id: 'impulse', label: 'Импульс', icon: 'fa-bolt-lightning' },
];

const NOTE = [
  { id: '', label: 'Все оценки', icon: 'fa-list' },
  { id: 'AB', label: 'A + B', icon: 'fa-star-half-stroke' },
  { id: 'A', label: 'Только A', icon: 'starOn' },
];

/** Filele: ce se apropie, ce e acum, ce a fost. */
const VIEWS = [
  { id: 'soon', label: 'Скоро', icon: 'fa-hourglass-half',
    title: 'Монеты, которые подходят к сильному уровню: подготовьтесь, решать будет стакан' },
  { id: 'live', label: 'Сейчас', icon: 'bolt',
    title: 'Формации на последней закрытой свече и сделки из «Скоро», которые идут' },
  { id: 'past', label: 'Прошедшие', icon: 'clockHistory',
    title: 'Закрытые сделки за 24 часа: чем закончился план' },
];

/** Ce control are sens pe ce filă. Restul se ascund — nu stau degeaba în bară. */
const TOOL_VIEWS = {
  preset: ['soon', 'live'], sort: ['soon', 'live'], filters: ['soon', 'live'],
  kind: ['live', 'past'], grade: ['live', 'past'], score: ['live', 'past'],
};
const SCORES = [50, 60, 70, 80, 90];
const RANG = { A: 0, B: 1, C: 2 };

/** Ce înseamnă fiecare cheie din `detail`, în cuvinte. */
const EXPLICATII = {
  atr_beyond: ['Закрытие за уровнем', (v) => `${v} ATR`],
  volume_x: ['Объём к среднему', (v) => `×${v}`],
  touches: ['Касаний уровня', (v) => String(v)],
  atr_expansion: ['Расширение ATR', (v) => `×${v}`],
  wick_body: ['Тень к телу', (v) => `×${v}`],
  range_atr: ['Диапазон', (v) => `${v} ATR`],
  close_pos: ['Закрытие у края свечи', (v) => `${Math.round(v * 100)}%`],
  natr: ['Ход за свечу', (v) => `${v}%`],
  flow: ['Приток', (v) => `×${v}`],
};

const KEY_UI = 'sx_formations_ui';

/**
 * Preseturile: filtre + sortare, cu praguri din cercetarea pe 15 741 de semnale (14 zile).
 * `hod` e mișcarea medie pe o lumânare de 5 minute, în procente (aceeași măsură ca acolo).
 */
const PRESETS = [
  { id: 'default', icon: 'fa-rotate-left', label: 'Стандарт', nota: '', sort: 'score',
    title: 'Как было до пресетов: все формации от 50 баллов, без фильтров, по баллам графика.' },
  { id: 'balanced', icon: 'fa-scale-balanced', label: 'Сбалансированный', nota: 'AB', sort: 'exp',
    title: 'Оценки A и B, лучшие по ожиданию сверху. Режим по умолчанию.' },
  { id: 'best', icon: 'trophy', label: 'Лучшие по истории', nota: '', sort: 'exp', maxFee: 0.15, minHod: 0.8,
    title: 'Комиссия ≤15% стопа и ход ≥0.8% за свечу. На 15 741 сигнале за 14 дней — группа '
      + 'ближе всех к нулю после комиссии; всё остальное заметно хуже.' },
  { id: 'volatile', icon: 'fire', label: 'Волатильные', nota: '', sort: 'hod', minHod: 1.2,
    title: 'Ход ≥1.2% за 5-минутную свечу: монеты, которые реально двигаются. Сортировка по ходу.' },
  { id: 'inflow', icon: 'fa-money-bill-trend-up', label: 'Приток денег', nota: '', sort: 'flow', minFlow: 1.5,
    title: 'Объём последних 90 минут ≥1.5× от нормы за 12 часов. Сортировка по притоку.' },
  { id: 'liquid', icon: 'fa-droplet', label: 'Ликвидные', nota: '', sort: 'vol', minVol: 50e6,
    title: 'Оборот ≥ $50M за сутки: узкий спред, легко войти и выйти. Сортировка по обороту.' },
  { id: 'onlyA', icon: 'starOn', label: 'Только A', nota: 'A', sort: 'exp',
    title: 'Только оценка A — то, что модель считает лучшим прямо сейчас.' },
];
/** Variantele fiecărui filtru; `undefined` = fără filtru. */
const FILTRE = [
  { key: 'minHod', icon: 'fa-wave-square', label: 'Ход/свеча', title: 'Средний ход за 5-минутную свечу, % от цены',
    opts: [[undefined, 'любой'], [0.5, '≥0.5%'], [0.8, '≥0.8%'], [1.2, '≥1.2%'], [2, '≥2%']] },
  { key: 'maxFee', icon: 'fa-percent', label: 'Комиссия', title: 'Какую часть стопа съедает комиссия Bybit за круг. Меньше — лучше: '
      + 'на истории сделки с комиссией >35% стопа теряли около 1R',
    opts: [[undefined, 'любая'], [0.15, '≤15% стопа'], [0.2, '≤20% стопа'], [0.35, '≤35% стопа']] },
  { key: 'minFlow', icon: 'fa-money-bill-trend-up', label: 'Приток', title: 'Объём последних 90 минут к норме за 12 часов',
    opts: [[undefined, 'любой'], [1.2, '≥1.2×'], [1.5, '≥1.5×'], [2, '≥2×']] },
  { key: 'minVol', icon: 'coins', label: 'Оборот', title: 'Оборот за 24 часа',
    opts: [[undefined, 'любой'], [10e6, '≥$10M'], [50e6, '≥$50M'], [100e6, '≥$100M']] },
  { key: 'minHist', icon: 'clockHistory', label: 'История', title: 'Средний итог сделок этой монеты за 7 дней (не меньше 5 сделок), в R после комиссии',
    opts: [[undefined, 'любая'], [0, '≥0R за 7д'], [0.2, '≥+0.2R за 7д']] },
];

/** Stările în care niciun ordin nu s-a umplut: nu sunt tranzacții, doar semnale trecute. */
const FARA_TRANZ = new Set(['expired', 'missed', 'skip']);

/** Istoria monedei pe scurt: 24 h și 7 zile, după comision. */
function coinText(c24, c7) {
  const R = (x) => `${x >= 0 ? '+' : ''}${x.toFixed(2)}R`;
  const parti = [];
  if (c24?.n) parti.push(`24ч: ${c24.n} сд. · ${Math.round(c24.plus * 100)}% в плюс · ${R(c24.E)}`);
  if (c7?.n) parti.push(`7д: ${c7.n} сд. · ${R(c7.E)}`);
  return parti.length ? `монета — ${parti.join(' | ')}` : 'монета: сделок ещё не было';
}

const SORTS = [
  { id: 'score', icon: 'candles', label: 'Баллы графика' },
  { id: 'exp', icon: 'fa-sack-dollar', label: 'Ожидание (R)' },
  { id: 'hod', icon: 'fa-wave-square', label: 'Ход/свеча' },
  { id: 'vol', icon: 'coins', label: 'Оборот' },
  { id: 'flow', icon: 'fa-money-bill-trend-up', label: 'Приток' },
  { id: 'dist', icon: 'fa-ruler-horizontal', label: 'Расстояние до уровня' },
  { id: 'time', icon: 'clock', label: 'Новые сверху' },
];

/** Mișcarea medie pe o lumânare de 5 minute, %. Peste tot aceeași măsură. */
function hod(it) {
  if (it.detail?.natr) return Number(it.detail.natr);
  if (it.atr && it.price) return it.atr / it.price * 100;
  return Number(it.natr) || 0;
}
const flux = (it) => Number(it.detail?.flow ?? it.aflux ?? 0);
/** Comisionul, ca parte din stop: la „Скоро" cel mai mic dintre cele două planuri. */
function feeR(it) {
  if (it.plan?.fee_r !== undefined) return it.plan.fee_r;
  const p = it.plans ? Object.values(it.plans).map((x) => x.fee_r).filter((x) => x !== undefined) : [];
  return p.length ? Math.min(...p) : null;
}

function candFmt(ts) {
  const s = Math.floor(Date.now() / 1000) - ts;
  if (s < 90) return 'сейчас';
  return `${Math.floor(s / 60)} мин назад`;
}

const semn = (v, cifre = 0) => `${v >= 0 ? '+' : ''}${Number(v).toFixed(cifre)}`;

/** Aceeași monedă poate da și „пробой", și „отскок" — cheia le ține separate. */
const cheia = (i) => `${i.exchange}|${i.market}|${i.symbol}|${i.kind}`;

/** Unde se deschide moneda în terminal: pe bursa contului, dacă o are listată. */
function bursaTiger(item) {
  const ales = tigerState().tradeExchange ?? 'bybit';
  if (!ales) return item.exchange;
  if (ales === 'bybit' && item.bybit === false) return item.exchange;
  return ales;
}

/** Un bip scurt, doar pentru semnalele A — un sunet la fiecare formație ar fi zgomot. */
function bip() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    bip.ctx = bip.ctx || new Ctx();
    const ctx = bip.ctx;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    for (const [f, d] of [[740, 0], [988, 0.11]]) {
      const t = ctx.currentTime + d;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.14, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
      osc.start(t); osc.stop(t + 0.22);
    }
  } catch { /* fără sunet nu cade nimic */ }
}

export class FormationsSection {
  constructor(app) {
    this.app = app;
    const ui = settings.get(KEY_UI, null) || {};
    this.kind = '';
    // Aceeaşi scară ca pe server: ponderile s-au rebalansat, deci 50 de acum
    // e cam ce însemna 55 înainte de a intra potenţialul în punctaj.
    this.minScore = Number(ui.minScore) || 50;
    this.nota = ui.nota ?? 'AB';
    // Fără preset salvat (prima deschidere sau setări de dinainte de preseturi): „Стандарт".
    this.preset = ui.preset ?? 'default';
    this.sort = ui.sort ?? 'score';
    this.flt = ui.flt ?? {};
    this.pastEmpty = ui.pastEmpty ?? false;
    if (!ui.preset) this.nota = '';
    this.auto = !!ui.auto;
    this.items = [];
    this.cards = new Map();
    /** Semnalele A deja anunțate în sesiunea asta — fiecare se anunță o singură dată. */
    this.anuntate = new Set();
    this.primaIncarcare = true;
    /** 'live' — formațiile de acum; 'past' — semnalele ieșite din listă, cu soarta lor. */
    this.view = 'live';
    this.past = [];
    this.liveCount = 0;
    /** Monedele care se apropie de un nivel — fila „Скоро". */
    this.soon = [];
    this.soonSound = ui.soonSound ?? true;
    /** De pe ce bursă sunt lumânările graficelor: 'binance' sau 'bybit'. */
    this.chartEx = ui.chartEx === 'bybit' ? 'bybit' : 'binance';
    this.soonAnuntate = new Set();
  }

  #saveUi() {
    settings.set(KEY_UI, {
      nota: this.nota, auto: this.auto, soonSound: this.soonSound, chartEx: this.chartEx,
      preset: this.preset, sort: this.sort, flt: this.flt, pastEmpty: this.pastEmpty, minScore: this.minScore,
    });
  }

  async mount(host) {
    this.root = el('div', { class: 'form-section' });

    this.autoEl = el('button', {
      class: `form-auto${this.auto ? ' on' : ''}`,
      title: 'Новый сетап с оценкой A сразу открывается в Tiger.Trade и подаёт звук',
      onclick: () => {
        this.auto = !this.auto;
        this.autoEl.classList.toggle('on', this.auto);
        this.#saveUi();
        // Contextul audio se trezește doar dintr-un gest al omului — acesta e gestul.
        if (this.auto) bip();
        toast(this.auto ? 'Авто: новые сетапы A откроются в Tiger.Trade' : 'Авто выключено', 'info');
      },
    }, [el('img', { class: 'tiger-mark', src: '/static/img/tiger.png', alt: '' }), el('span', { text: 'Авто A' })]);

    this.metaEl = el('span', { class: 'reports-hint' });
    this.statsEl = el('div', { class: 'form-stats' });
    this.listEl = el('div', { class: 'form-list' });
    // O formație dispare din listă când momentul ei a trecut (scanerul privește doar ultima
    // lumânare închisă), nu când a devenit greșită. Aici rămâne, cu ce s-a întâmplat după.
    this.pastEl = el('div', { class: 'form-past' });
    this.pastEl.hidden = true;
    // „Скоро": nu prezice direcția (măsurat: 46% proboi / 53% ricoșeu, nimic nu mută
    // balanța), ci spune unde se va decide, ca omul să fie deja cu graficul deschis.
    this.soonEl = el('div', { class: 'form-soon' });
    this.soonEl.hidden = true;
    this.soonSoundEl = el('button', {
      class: `form-auto${this.soonSound ? ' on' : ''}`,
      title: 'Звук, когда цена подходит к уровню вплотную (≤ 0.15 ATR)',
      onclick: () => {
        this.soonSound = !this.soonSound;
        this.soonSoundEl.classList.toggle('on', this.soonSound);
        this.#saveUi();
        if (this.soonSound) bip();
      },
    }, [icon('bell', 12), el('span', { text: 'Звук «вплотную»' })]);
    this.soonSoundEl.hidden = true;
    this.viewEl = el('div', { class: 'ideas-tabs form-views fx-tabs' }, VIEWS.map((v) => el('button', {
      class: `ideas-tab fx-tab${v.id === this.view ? ' active' : ''}`, dataset: { id: v.id }, title: v.title,
      onclick: () => this.#switchView(v.id),
    }, [icon(v.icon, 12), el('span', { text: v.label }), el('span', { class: 'fx-count', text: '0' })])));
    this.emptyEl = el('div', { class: 'ideas-empty' });

    this.root.append(
      el('div', { class: 'fx-head' }, [
        el('div', { class: 'fx-row' }, [
          el('h2', { class: 'ideas-title fx-title' }, [icon('layerGroup', 14), el('span', { text: 'Формации' })]),
          this.viewEl,
          el('span', { class: 'fx-spacer' }),
          this.autoEl,
          this.soonSoundEl,
          this.#exSwitch(),
        ]),
        el('div', { class: 'fx-row fx-tools' }, [
          ...this.#toolbar(),
          el('span', { class: 'fx-spacer' }),
          this.metaEl,
        ]),
      ]),
      this.statsEl,
      this.listEl,
      this.pastEl,
      this.soonEl,
    );
    this.#syncTools();
    host.append(this.root);

    await Promise.all([this.#load(), this.#loadStats()]);
    // Formațiile se rescanează la 90 s, dar fluxul și nota se schimbă de la o secundă
    // la alta — de aceea cerem des; răspunsul e din memorie, ieftin.
    this.timer = setInterval(() => this.#load(), 10000);
    this.statsTimer = setInterval(() => this.#loadStats(), 120000);
    this.#loadPast();
    this.pastTimer = setInterval(() => this.#loadPast(), 30000);
  }

  #switchView(v) {
    this.view = v;
    for (const b of this.viewEl.children) b.classList.toggle('active', b.dataset.id === v);
    this.listEl.hidden = v !== 'live';
    this.pastEl.hidden = v !== 'past';
    this.soonEl.hidden = v !== 'soon';
    this.autoEl.hidden = v !== 'live';
    this.soonSoundEl.hidden = v !== 'soon';
    this.#syncTools();
    if (v === 'past') this.#loadPast();
    else if (v === 'soon') this.#renderSoon();
    else this.#render();
  }

  async #loadPast() {
    try {
      const d = await (await fetch('/api/setups/recent?hours=24')).json();
      this.past = d.items || [];
      this.pastSrc = { exchange: d.exchange, market: d.market };
    } catch {
      this.past = [];
    }
    this.#renderPast();
  }

  /** Semnalele trecute: tot ce nu mai e acum în listă, filtrat la fel ca lista. */
  #pastVisible(cuGoale = this.pastEmpty) {
    const acum = new Set(this.items.map((i) => `${i.symbol}|${i.kind}|${i.direction}|${i.found_at}`));
    return this.past
      .filter((r) => cuGoale || !FARA_TRANZ.has(r.status))
      .filter((r) => !acum.has(`${r.symbol}|${r.kind}|${r.direction}|${r.found_at}`))
      .filter((r) => (!this.kind || r.kind === this.kind) && (r.kind === 'approach' || r.score >= this.minScore))
      .filter((r) => r.kind === 'approach' || !this.nota || this.nota.includes(r.grade || 'C'));
  }

  #renderPast() {
    const vizibile = this.#pastVisible();
    this.#syncCounts();
    if (this.view !== 'past') return;
    clear(this.pastEl);
    if (!vizibile.length) {
      this.pastEl.append(el('div', { class: 'ideas-empty' }, [
        el('div', { text: 'Пока пусто.' }),
        el('div', {
          class: 'ideas-empty-sub',
          text: 'Сюда попадают сигналы, которые ушли из списка за последние 24 часа, '
            + 'с тем, чем закончилась сделка по плану.',
        }),
      ]));
      return;
    }
    this.pastEl.append(this.#pastSummary(vizibile));
    this.pastEl.append(el('div', { class: 'fp-row fp-head' },
      ['Время', 'Монета', 'Сетап', 'Оценка', 'Итог по плану', 'Макс. в плюс', ''].map((t) => el('span', { text: t }))));
    for (const r of vizibile) this.pastEl.append(this.#pastRow(r));
  }

  /** Rezumatul filei: câte tranzacții reale, cât au dat, și comutatorul pentru cele fără ordin. */
  #pastSummary(vizibile) {
    const inchise = vizibile.filter((r) => ['win', 'loss', 'timeout', 'partial'].includes(r.status) && r.net_r !== null && r.net_r !== undefined);
    const goale = this.#pastVisible(true).filter((r) => FARA_TRANZ.has(r.status)).length;
    const E = inchise.length ? inchise.reduce((s, r) => s + r.net_r, 0) / inchise.length : null;
    const plus = inchise.length ? Math.round(inchise.filter((r) => r.net_r > 0).length / inchise.length * 100) : 0;
    const R = (x) => `${x >= 0 ? '+' : ''}${x.toFixed(2)}R`;
    return el('div', { class: 'fp-summary' }, [
      el('span', {
        text: inchise.length
          ? `За 24 ч: ${inchise.length} закрытых сделок · ${plus}% в плюс · в среднем ${R(E)} на сделку после комиссии`
          : 'За 24 ч закрытых сделок нет',
      }),
      el('label', { class: 'fp-toggle', title: 'Сигналы, по которым ни один ордер не исполнился: цена ушла от уровня, пробоя не было, вход пропущен' }, [
        el('input', {
          type: 'checkbox', checked: this.pastEmpty,
          onchange: (e) => { this.pastEmpty = e.target.checked; this.#saveUi(); this.#renderPast(); },
        }),
        el('span', { text: `показывать без сделки (${goale})` }),
      ]),
    ]);
  }

  #pastRow(r) {
    const lung = r.direction === 'long';
    const ora = new Date(r.found_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const R = (x) => `${x >= 0 ? '+' : ''}${Number(x).toFixed(2)}R`;
    const stari = {
      win: ['win', `✓ цель ${R(r.net_r ?? 0)}`],
      loss: ['loss', `✕ стоп ${R(r.net_r ?? 0)}`],
      timeout: [(r.net_r ?? 0) >= 0 ? 'win' : 'loss', `⏱ выход через час ${R(r.net_r ?? 0)}`],
      partial: ['win', `½ на цели 1, остаток в безубыток ${R(r.net_r ?? 0)}`],
      expired: ['mute', 'цена ушла от уровня — ни один ордер не исполнен за 30 мин'],
      now: ['open', r.cur_r !== null && r.cur_r !== undefined ? `в работе, сейчас ${R(r.cur_r)}` : 'в работе'],
      missed: ['mute', 'не сработал: пробоя не было'],
      skip: ['mute', 'пропуск: цена ушла за стоп до входа'],
    };
    let [cls, text] = stari[r.status] || ['open', 'в работе'];
    if (r.status === 'open') {
      if (!r.entry) text = r.entry_type === 'stop' ? 'ждёт пробоя для входа' : 'вход на следующей минуте';
      else if (r.cur_r !== null && r.cur_r !== undefined) text = `в работе, сейчас ${R(r.cur_r)}`;
    }
    const e = r.expected_r;
    const item = this.#itemFromPast(r);
    return el('div', { class: 'fp-row', onclick: () => this.#openPast(r, item), title: 'Открыть большой график с планом' }, [
      el('span', { class: 'fp-time', text: ora }),
      el('span', { class: 'fp-coin' }, [iconMoneda(r.symbol, 15), el('b', { text: baseTicker(r.symbol) })]),
      el('span', { class: 'fp-setup' }, [
        el('span', { class: `idea-dir ${lung ? 'long' : 'short'}`, text: lung ? 'LONG' : 'SHORT' }),
        el('span', { text: r.label }),
      ]),
      el('span', { class: 'fp-grade' }, [
        el('span', { class: `form-grade g${r.grade || 'C'}`, text: r.grade || '—' }),
        e === null || e === undefined ? null : el('span', { class: `form-exp ${e > 0 ? 'up' : 'down'}`, text: R(e) }),
      ]),
      el('span', { class: `fp-status ${cls}`, text }),
      el('span', {
        class: 'fp-mfe', text: r.mfe_r ? `до +${Number(r.mfe_r).toFixed(1)}R` : '—',
        title: 'Насколько далеко цена ходила в нашу сторону после входа',
      }),
      el('span', { class: 'fp-act' }, [tigerBtn(r.symbol, bursaTiger(item), item.market), icon('expand', 12)]),
    ]);
  }

  /** O tranzacție pornită dintr-o apropiere, ca „formație" pentru cardul din „Сейчас". */
  #itemFromPlay(r) {
    const R = (x) => `${x >= 0 ? '+' : ''}${Number(x).toFixed(2)}R`;
    return {
      symbol: r.symbol, exchange: this.snap?.exchange || 'binance', market: this.snap?.market || 'futures',
      tf: '5m', kind: 'approach_trade', direction: r.direction, score: null,
      label: `${r.label}${r.cur_r !== null && r.cur_r !== undefined ? ` · сейчас ${R(r.cur_r)}` : ''}`,
      level: r.level, price: r.price, found_at: r.found_at, quoteVolume: r.quoteVolume || 0,
      natr: r.natr || 0, detail: {}, flow: r.flow, aflux: r.aflux,
      distance_pct: r.price ? Math.round((r.price - r.level) / r.level * 10000) / 100 : 0,
      plan: r.plan, grade: { grade: null, pro: [`ордер ${r.which === 'bounce' ? 'отскока' : 'пробоя'} исполнен — сделка идёт`], contra: [] },
      bybit: r.bybit, coin24: r.coin24, coin7: r.coin7,
    };
  }

  /** Din rândul jurnalului, o „formație" pe care vederea mare o poate desena. */
  #itemFromPast(r) {
    const src = this.pastSrc || {};
    if (r.kind === 'approach') {
      // Apropiere fără tranzacție: arătăm ambele planuri; cu tranzacție: planul ales.
      return {
        symbol: r.symbol, exchange: src.exchange || 'binance', market: src.market || 'futures',
        tf: '5m', kind: r.which ? 'approach_trade' : 'approach', label: r.label, direction: r.direction,
        score: null, level: r.level, level_kind: r.level_kind, touches: r.touches, dist_atr: r.dist_atr,
        found_at: r.found_at, price: r.price, quoteVolume: 0,
        plans: r.which ? undefined : r.plans, plan: r.which ? r.plan : null, grade: null,
      };
    }
    return {
      symbol: r.symbol, exchange: src.exchange || 'binance', market: src.market || 'futures',
      tf: r.tf || '5m', kind: r.kind, label: r.label, direction: r.direction, score: r.score,
      level: r.level, found_at: r.found_at, price: null, quoteVolume: 0, bybit: undefined,
      plan: {
        type: r.entry_type, entry: r.entry || r.plan_entry, stop: r.stop, target: r.target, target1: r.target1,
        fee_r: r.fee_r_plan ?? r.fee_r, stop_pct: r.stop_pct,
      },
      grade: { grade: r.grade, E: r.expected_r, pro: [], contra: [] },
    };
  }

  #openPast(r, item) {
    const ora = new Date(r.found_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    openSetupView(item, {
      app: this.app,
      chartExchange: this.#chartEx(item),
      tigerExchange: bursaTiger(item),
      latest: () => item,
      history: () => {
        const nou = this.past.find((x) => x.id === r.id) || r;
        const st = {
          now: 'сделка ещё идёт', expired: 'цена ушла от уровня, ордера не исполнены',
          win: 'обе цели достигнуты', partial: 'половина на цели 1, остаток в безубыток',
          loss: 'выбит стоп', timeout: 'закрыт по времени',
          missed: 'пробоя не было', skip: 'вход пропущен', open: 'сделка ещё идёт',
        }[nou.status] || nou.status;
        const rez = nou.net_r !== null && nou.net_r !== undefined
          ? ` (${nou.net_r >= 0 ? '+' : ''}${nou.net_r.toFixed(2)}R)`
          : nou.cur_r !== null && nou.cur_r !== undefined
            ? ` (сейчас ${nou.cur_r >= 0 ? '+' : ''}${nou.cur_r.toFixed(2)}R)` : '';
        return { status: nou.status, text: `Из истории: найден в ${ora}, уже не в списке — ${st}${rez}` };
      },
    });
  }

  /** Sunet o singură dată per apropiere, când prețul ajunge lângă nivel. */
  #anuntaSoon() {
    for (const a of this.soon) {
      if (!a.imminent) continue;
      const k = `${a.symbol}|${a.level_kind}|${a.found_at}`;
      if (this.soonAnuntate.has(k)) continue;
      this.soonAnuntate.add(k);
      if (this.primaIncarcare || !this.soonSound) continue;
      bip();
      toast(`Вплотную к уровню: ${baseTicker(a.symbol)} · ${fmtPrice(a.level)} — откройте график`, 'info');
    }
  }

  #renderSoon() {
    this.#syncCounts();
    if (this.view !== 'soon') return;
    if (!this.soonList) {
      // Nota și containerul se construiesc o dată; cardurile se actualizează prin
      // diferență, ca graficele lor să rămână vii între reîmprospătări.
      this.soonEmpty = el('div', { class: 'ideas-empty' }, [el('div', { text: 'Сейчас ни одна монета не подходит к сильному уровню.' })]);
      this.soonList = el('div', { class: 'fs-list' });
      this.soonCards = new Map();
      this.soonEl.append(el('div', { class: 'fs-note' }, [
        el('b', { text: 'Это подготовка, а не прогноз. ' }),
        'По 55 тыс. подходов к уровням за 14 дней: пробой в 46% случаев, отскок в 53%, и ни поток, '
          + 'ни OI, ни объём, ни тренд это не меняют. Оба плана, выставленные вслепую, исторически убыточны. '
          + 'Здесь — где будет решаться. Решайте по стакану в момент касания: плотность на уровне '
          + 'разъедают → план пробоя; стоит и цена вязнет → план отскока; сняли → не входить.',
      ]), this.soonList);
    }
    const cheieSoon = (a) => `${a.symbol}|${a.level_kind}`;
    const lista = this.#sorted(this.soon.filter((a) => this.#passes(a)), 'dist');
    const dorite = new Map(lista.map((a) => [cheieSoon(a), a]));
    for (const [k, card] of this.soonCards) {
      if (!dorite.has(k)) { this.#destroyCard(card); this.soonCards.delete(k); }
    }
    for (const [k, a] of dorite) {
      const c = this.soonCards.get(k);
      if (c) c.refresh(a);
      else this.soonCards.set(k, this.#soonCard(a));
    }
    for (const k of dorite.keys()) this.soonList.append(this.soonCards.get(k).element);
    if (lista.length) this.soonEmpty.remove(); else this.soonList.append(this.soonEmpty);
  }

  /** O apropiere ca „formație" pentru grafic și vederea mare: fără plan unic, cu două. */
  #soonItem(a) {
    return { ...a, direction: a.level_kind === 'resistance' ? 'long' : 'short', score: 0, grade: null, plan: null };
  }

  #soonCard(a0) {
    const R = (x) => (x === null || x === undefined ? '—' : `${x >= 0 ? '+' : ''}${Number(x).toFixed(2)}R`);
    const P = (x) => (x === null || x === undefined ? '—' : `${Math.round(x * 100)}%`);
    const badgeEl = el('span', { class: 'fs-badge', text: 'вплотную' });
    const labelEl = el('span', { class: 'form-label' });
    const nobybitEl = el('span', { class: 'form-nobybit', text: 'нет на Bybit' });
    const tigerSlot = el('span', { class: 'form-tiger' });
    const nivelEl = el('span', { class: 'form-num-v' });
    const distEl = el('span', { class: 'form-num-v' });
    const distAtrEl = el('span', { class: 'form-plan-sub' });
    const atingEl = el('span', { class: 'form-num-v' });
    const hodEl = el('span', { class: 'form-num-v' });
    const volEl = el('span', { class: 'form-num-v' });
    const chgEl = el('span', { class: 'form-num-v' });
    const planuriEl = el('div', { class: 'fs-plans' });
    const fluxEl = el('div', { class: 'form-flow' });
    const coinEl = el('div', {
      class: 'fs-coin24',
      title: 'Как отработали сделки этой монеты за 24 часа и 7 дней, после комиссии.',
    });
    const chartEl = el('div', { class: 'form-chart', title: 'Открыть большой график' });
    const num = (k, v) => el('div', { class: 'form-num' }, [el('span', { class: 'form-num-k', text: k }), v]);

    const card = {
      chart: null, observer: null, linii: [], semnatura: null, item: this.#soonItem(a0),
    };
    const deschide = () => this.#openSoon(card.item);
    card.element = el('article', { class: 'fs-card' }, [
      el('div', { class: 'form-top' }, [
        badgeEl,
        ...coinButtons(a0, () => this.app.openInstrument(card.item)),
        tigerSlot,
        el('span', { class: 'form-spacer' }),
        el('button', { class: 'form-expand fs-expand', title: 'Большой график с обоими планами, плотностями и крупными сделками', onclick: deschide },
          [icon('expand', 12)]),
      ]),
      el('div', { class: 'form-sub' }, [labelEl, nobybitEl]),
      el('div', { class: 'form-nums sase' }, [
        num('Уровень', nivelEl), el('div', { class: 'form-num' }, [
          el('span', { class: 'form-num-k', text: 'До уровня' }), distEl, distAtrEl,
        ]), num('Касаний', atingEl),
        num('Ход/свеча', hodEl), num('Оборот', volEl), num('Изм. 24ч', chgEl),
      ]),
      planuriEl,
      fluxEl,
      coinEl,
      chartEl,
      miniLegend('approach'),
    ]);
    chartEl.addEventListener('click', deschide);

    card.refresh = (a) => {
      card.item = this.#soonItem(a);
      const rez = a.level_kind === 'resistance';
      card.element.className = `fs-card${a.imminent ? ' imminent' : ''}`;
      badgeEl.hidden = !a.imminent;
      labelEl.textContent = a.label;
      nobybitEl.hidden = a.bybit !== false;
      clear(tigerSlot);
      const btn = tigerBtn(a.symbol, bursaTiger(a), a.market);
      if (btn) tigerSlot.append(btn);
      nivelEl.textContent = fmtPrice(a.level);
      distEl.textContent = `${a.dist_pct}%`;
      distAtrEl.textContent = `${a.dist_atr} ATR`;
      atingEl.textContent = String(a.touches);
      hodEl.textContent = hod(a) ? `${hod(a).toFixed(2)}%` : '—';
      hodEl.title = 'Средний ход за 5-минутную свечу (ATR), % от цены';
      volEl.textContent = a.quoteVolume ? fmtVolume(a.quoteVolume) : '—';
      const ch = a.change24h;
      chgEl.className = `form-num-v ${ch > 0 ? 'up' : ch < 0 ? 'down' : ''}`;
      chgEl.textContent = ch === null || ch === undefined ? '—' : `${ch >= 0 ? '+' : ''}${Number(ch).toFixed(2)}%`;
      clear(planuriEl);
      const st = a.stats || {};
      for (const [nume, p, s2, cls] of [
        [rez ? 'Пробой ↑ LONG' : 'Пробой ↓ SHORT', a.plans.breakout, st.breakout, 'brk'],
        [rez ? 'Отскок ↓ SHORT' : 'Отскок ↑ LONG', a.plans.bounce, st.bounce, 'bnc'],
      ]) {
        // Două planuri alăturate, fiecare pe patru rânduri scurte: cardul rămâne jos, iar
        // prețurile încap întregi și în jumătatea de card. Textul lung — în title.
        const tip = p.type === 'stop' ? 'стоп-ордер' : 'лимит';
        const intreg = `${nume}: ${tip} ${fmtPrice(p.entry)} · стоп ${fmtPrice(p.stop)} · `
          + `цель 1 (½) ${fmtPrice(p.target1 ?? p.target)} · цель 2 ${fmtPrice(p.target)}`;
        planuriEl.append(el('div', { class: `fs-plan ${cls}`, title: intreg }, [
          el('b', { text: nume }),
          el('span', { text: `вход ${fmtPrice(p.entry)} · стоп ${fmtPrice(p.stop)}` }),
          el('span', { text: `ц1 ${fmtPrice(p.target1 ?? p.target)} · ц2 ${fmtPrice(p.target)}` }),
          el('span', {
            class: 'fs-hist',
            title: 'Если ставить этот план на каждом таком подходе вслепую: как часто срабатывает и средний итог после комиссии',
            text: s2?.E === null || s2?.E === undefined
              ? 'вслепую: после калибровки'
              : `вслепую ${P(s2?.triggered)} · ${R(s2?.E)}`,
          }),
        ]));
      }
      renderFlow(fluxEl, a.flow, rez ? 'long' : 'short');
      coinEl.textContent = coinText(a.coin24, a.coin7);
      // Liniile se mută doar când s-a mutat nivelul — nu la fiecare citire.
      const sem = `${a.level}|${a.plans.breakout.entry}|${a.plans.bounce.entry}`;
      if (sem !== card.semnatura) {
        card.semnatura = sem;
        card.linii = drawPlanLines(card.chart, card.item, chartTheme().levelLine, card.linii);
      }
    };
    card.refresh(a0);

    // Graficul se naște când cardul intră în ecran, ca la formații.
    card.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || card.chart) continue;
        card.chart = new Chart(chartEl, {
          exchange: this.#chartEx(a0), market: a0.market, symbol: a0.symbol, tf: a0.tf || '5m',
          compact: true, limit: 200, indicators: { volume: true }, showLevels: false,
        });
        card.chart.load()
          .then(() => { card.linii = drawPlanLines(card.chart, card.item, chartTheme().levelLine, card.linii); })
          .catch(() => chartEl.append(el('div', { class: 'card-error', text: 'Не удалось загрузить свечи' })));
        card.observer.disconnect();
      }
    }, { root: this.soonEl, rootMargin: '250px' });
    card.observer.observe(card.element);
    return card;
  }

  #openSoon(item) {
    openSetupView(item, {
      app: this.app,
      chartExchange: this.#chartEx(item),
      tigerExchange: bursaTiger(item),
      latest: () => {
        const nou = this.soon.find((x) => x.symbol === item.symbol && x.level_kind === item.level_kind);
        return nou ? this.#soonItem(nou) : item;
      },
      history: () => (this.soon.some((x) => x.symbol === item.symbol && x.level_kind === item.level_kind)
        ? null
        : { status: 'gone', text: 'Цена отошла от уровня или уже прошла его — подход закончился.' }),
    });
  }

  /**
   * Bursa graficului unei monede: cea aleasă în antet, dacă moneda e listată acolo.
   * Scanerul caută pe Binance; Bybit e doar sursa lumânărilor, iar o monedă absentă de
   * pe Bybit rămâne pe Binance (cardul o spune prin „нет на Bybit").
   */
  #chartEx(item) {
    if (this.chartEx === 'bybit' && item?.bybit !== false) return 'bybit';
    return item?.exchange || 'binance';
  }

  #exSwitch() {
    this.exEl = el('div', {
      class: 'fx-seg',
      title: 'Свечи на графиках: Binance или Bybit. Формации ищутся на Binance — там больше объёма; '
        + 'уровни и планы рисуются поверх свечей выбранной биржи.',
    }, [el('span', { class: 'fx-seg-k' }, [icon('chartLine', 11)]), ...[['binance', 'Binance'], ['bybit', 'Bybit']].map(([id, nume]) => el('button', {
      class: `fx-seg-btn${this.chartEx === id ? ' active' : ''}`,
      dataset: { id },
      onclick: () => {
        if (this.chartEx === id) return;
        this.chartEx = id;
        this.#saveUi();
        for (const b of this.exEl.querySelectorAll('button')) b.classList.toggle('active', b.dataset.id === id);
        this.#rebuildCharts();
      },
    }, [iconBursa(id, 14), el('span', { text: nume })]))]);
    return this.exEl;
  }

  /** Graficele se refac pe bursa nouă; cardurile își păstrează restul. */
  #rebuildCharts() {
    for (const card of this.cards.values()) this.#destroyCard(card);
    this.cards.clear();
    for (const card of this.soonCards?.values() || []) this.#destroyCard(card);
    this.soonCards?.clear();
    this.#render();
    this.#renderSoon();
  }

  /** Bara de unelte: dropdown-uri cu iconițe, butonul de filtre și filtrele active. */
  #toolbar() {
    const it = (value, label, ic) => ({ value, label, icon: ic });
    this.presetDD = dropdown({
      className: 'fx-dd', lead: 'sliders', prefix: 'Пресет',
      title: PRESETS.find((p) => p.id === this.preset)?.title || 'Свои настройки',
      items: [...PRESETS.map((p) => it(p.id, p.label, p.icon)), it('custom', 'Свой', 'pencil')],
      value: this.preset,
      onSelect: (v) => this.#applyPreset(v),
    });
    this.sortDD = dropdown({
      className: 'fx-dd', lead: 'fa-arrow-down-wide-short', prefix: 'Сортировка', title: 'Порядок карточек',
      items: SORTS.map((x) => it(x.id, x.label, x.icon)),
      value: this.sort,
      onSelect: (v) => { this.sort = v; this.#custom(); this.#saveUi(); this.#render(); this.#renderSoon(); },
    });
    this.kindDD = dropdown({
      className: 'fx-dd', lead: 'fa-shapes', prefix: 'Сетап', title: 'Какие формации показывать',
      items: FELURI.map((f) => it(f.id, f.label, f.icon)),
      value: this.kind,
      onSelect: (v) => { this.kind = v; this.#render(); this.#renderPast(); },
    });
    this.gradeDD = dropdown({
      className: 'fx-dd', lead: 'medal', prefix: 'Оценка',
      title: 'Оценка модели: ожидание в R после комиссии. A ≥ +0.25R, B ≥ +0.05R',
      items: NOTE.map((n) => it(n.id, n.label, n.icon)),
      value: this.nota,
      onSelect: (v) => { this.nota = v; this.#custom(); this.#saveUi(); this.#render(); this.#renderPast(); },
    });
    this.scoreDD = dropdown({
      className: 'fx-dd', lead: 'fa-gauge-high', prefix: 'Баллы', title: 'Минимальные баллы формации по графику',
      items: SCORES.map((x) => it(String(x), `от ${x}`)),
      value: String(this.minScore),
      onSelect: (v) => { this.minScore = Number(v); this.#saveUi(); this.#render(); this.#renderPast(); },
    });
    this.fltCount = el('span', { class: 'fx-badge' });
    this.fltBtn = el('button', {
      class: 'fx-filters', title: 'Ход, комиссия, приток, оборот и история монеты',
      onclick: (e) => { e.stopPropagation(); this.#toggleFilters(); },
    }, [icon('filter', 11), el('span', { text: 'Фильтры' }), this.fltCount, icon('chevronDown', 10)]);
    this.chipsBox = el('div', { class: 'fx-chips' });
    this.fltPanel = this.#filterPanel();
    this.tools = {
      preset: [this.presetDD], sort: [this.sortDD], filters: [this.fltBtn, this.chipsBox],
      kind: [this.kindDD], grade: [this.gradeDD], score: [this.scoreDD],
    };
    this.#syncFlt();
    return [this.presetDD, this.sortDD, this.kindDD, this.gradeDD, this.scoreDD, this.fltBtn, this.chipsBox];
  }

  /** Pe fiecare filă rămân doar controalele care schimbă ceva acolo. */
  #syncTools() {
    for (const [k, els] of Object.entries(this.tools || {})) {
      for (const x of els) x.hidden = !TOOL_VIEWS[k].includes(this.view);
    }
    if (this.view === 'past') this.#closeFilters();
  }

  /** Panoul de filtre: fiecare filtru e un rând de variante — un clic, fără liste derulante. */
  #filterPanel() {
    this.fltRows = {};
    const panel = el('div', { class: 'fx-panel', onmousedown: (e) => e.stopPropagation() });
    panel.append(el('div', { class: 'fx-panel-head' }, [
      el('span', { class: 'fx-panel-title' }, [icon('filter', 12), el('span', { text: 'Фильтры' })]),
      el('button', { class: 'fx-reset', onclick: () => this.#setFilters({}) }, [icon('undo', 10), el('span', { text: 'Сбросить все' })]),
      el('button', { class: 'fx-x', title: 'Закрыть', onclick: () => this.#closeFilters() }, [icon('close', 12)]),
    ]));
    for (const f of FILTRE) {
      const pills = f.opts.map(([v, t]) => el('button', {
        class: 'fx-pill', text: t, dataset: { v: v === undefined ? '' : String(v) },
        onclick: () => this.#setFilter(f.key, v),
      }));
      const hint = el('div', { class: 'fx-frow-hint', text: f.title });
      this.fltRows[f.key] = { pills, hint };
      panel.append(el('div', { class: 'fx-frow' }, [
        el('div', { class: 'fx-frow-k' }, [icon(f.icon, 11), el('span', { text: f.label })]),
        el('div', { class: 'fx-pills' }, pills),
        hint,
      ]));
    }
    panel.append(el('div', { class: 'fx-panel-foot', text: 'Фильтры действуют на «Скоро» и «Сейчас». Выбор сохраняется.' }));
    return panel;
  }

  #toggleFilters() {
    if (this.fltPanel.isConnected) { this.#closeFilters(); return; }
    // Pe <body>, cu poziție fixă — ca dropdown-urile: antetul nu-l taie.
    document.body.append(this.fltPanel);
    const r = this.fltBtn.getBoundingClientRect();
    const w = Math.min(470, window.innerWidth - 16);
    this.fltPanel.style.width = `${w}px`;
    this.fltPanel.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - w - 8))}px`;
    this.fltPanel.style.top = `${r.bottom + 6}px`;
    this.fltBtn.classList.add('open');
    this.fltOff = (e) => {
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      if (e.type === 'mousedown' && this.fltBtn.contains(e.target)) return;
      this.#closeFilters();
    };
    document.addEventListener('mousedown', this.fltOff);
    document.addEventListener('keydown', this.fltOff);
    window.addEventListener('resize', this.fltOff);
  }

  #closeFilters() {
    if (!this.fltPanel?.isConnected) return;
    this.fltPanel.remove();
    this.fltBtn.classList.remove('open');
    document.removeEventListener('mousedown', this.fltOff);
    document.removeEventListener('keydown', this.fltOff);
    window.removeEventListener('resize', this.fltOff);
  }

  #setFilter(key, v) {
    this.#setFilters({ ...this.flt, [key]: v });
  }

  #setFilters(flt) {
    this.flt = flt;
    this.#custom();
    this.#syncFlt();
    this.#saveUi();
    this.#render();
    this.#renderSoon();
  }

  #applyPreset(id) {
    const p = PRESETS.find((x) => x.id === id);
    this.preset = id;
    this.presetDD?.setValue(id);
    const btn = this.presetDD?.querySelector('.dd-button');
    if (btn) btn.title = p?.title || 'Свои настройки';
    if (p) {
      this.nota = p.nota;
      this.sort = p.sort;
      this.flt = { maxFee: p.maxFee, minHod: p.minHod, minFlow: p.minFlow, minVol: p.minVol };
      this.sortDD?.setValue(p.sort);
    }
    this.#syncFlt();
    this.#saveUi();
    this.#syncChips();
    this.#render();
    this.#renderSoon();
    this.#renderPast();
  }

  /** O schimbare de mână: presetul devine „Свой", filtrele lui rămân. */
  #custom() {
    if (this.preset === 'custom') return;
    this.preset = 'custom';
    this.presetDD?.setValue('custom');
    const btn = this.presetDD?.querySelector('.dd-button');
    if (btn) btn.title = 'Свои настройки';
  }

  /** Starea filtrelor peste tot: variantele din panou, numărul pe buton, cipurile active. */
  #syncFlt() {
    if (!this.chipsBox) return;
    const f = this.flt || {};
    let n = 0;
    clear(this.chipsBox);
    for (const def of FILTRE) {
      const v = f[def.key];
      const cheie = v === undefined || v === null ? '' : String(v);
      for (const p of this.fltRows?.[def.key]?.pills || []) p.classList.toggle('active', p.dataset.v === cheie);
      if (cheie === '') continue;
      n++;
      const t = def.opts.find(([x]) => x !== undefined && String(x) === cheie)?.[1] ?? cheie;
      this.chipsBox.append(el('button', {
        class: 'fx-chip', title: 'Убрать этот фильтр', onclick: () => this.#setFilter(def.key, undefined),
      }, [icon(def.icon, 10), el('span', { text: `${def.label} ${t}` }), icon('close', 9)]));
    }
    if (n > 1) {
      this.chipsBox.append(el('button', { class: 'fx-chip-reset', text: 'сбросить', onclick: () => this.#setFilters({}) }));
    }
    this.fltCount.textContent = String(n);
    this.fltCount.hidden = !n;
    this.fltBtn.classList.toggle('on', n > 0);
  }

  /** Sub filtrul „История": cât au dat, după aceea, monedele cu istorie bună față de restul. */
  #syncHistCheck(c) {
    const row = this.fltRows?.minHist;
    if (!row) return;
    const R = (x) => `${x >= 0 ? '+' : ''}${x.toFixed(2)}R`;
    if (!c?.good?.n || !c?.rest?.n) {
      row.hint.textContent = 'Проверка: истории пока мало, чтобы сказать, помогает ли фильтр.';
      return;
    }
    let verdict;
    if (c.days < 3 || c.good.n < 100) verdict = 'Истории пока меньше 3 дней — вывод делать рано.';
    else if (c.good.E - c.rest.E <= 0.05) verdict = 'Разница мала — фильтр почти ничего не даёт.';
    else if (c.good.E <= 0) verdict = 'Монеты с хорошей историей теряют меньше, но всё равно в минусе.';
    else verdict = 'Фильтр помогает: монеты с хорошей историей и дальше в плюсе.';
    row.hint.textContent = `Проверка за ${c.days} дн.: монеты с историей ≥0R дальше дали ${R(c.good.E)} `
      + `(${c.good.n} сд.), остальные ${R(c.rest.E)} (${c.rest.n} сд.). ${verdict}`;
  }

  #passes(it) {
    const f = this.flt || {};
    if (f.maxFee !== undefined) { const fr = feeR(it); if (fr === null || fr > f.maxFee) return false; }
    if (f.minHod !== undefined && hod(it) < f.minHod) return false;
    if (f.minFlow !== undefined && flux(it) < f.minFlow) return false;
    if (f.minVol !== undefined && (Number(it.quoteVolume) || 0) < f.minVol) return false;
    if (f.minHist !== undefined) {
      const c = it.coin7;
      if (!c || c.n < 5 || c.E < f.minHist) return false;
    }
    return true;
  }

  /** Sortarea aleasă; `implicit` e cea a filei când alegerea nu se aplică (la „Скоро" — distanța). */
  #sorted(list, implicit = 'exp') {
    // La „Скоро" nu există notă sau punctaj: acolo ele înseamnă „cel mai aproape de nivel".
    const k = (this.sort === 'exp' || this.sort === 'score') && implicit === 'dist' ? 'dist' : this.sort;
    const key = {
      score: (i) => i.score ?? 0,
      exp: (i) => -((RANG[i.grade?.grade] ?? 3) * 1000 - (i.grade?.points ?? i.score ?? 0)),
      hod: (i) => hod(i),
      vol: (i) => Number(i.quoteVolume) || 0,
      flow: (i) => flux(i),
      dist: (i) => -(i.dist_atr ?? Math.abs(i.distance_pct ?? 0)),
      time: (i) => i.found_at || 0,
    }[k] || ((i) => i.found_at || 0);
    return [...list].sort((a, b) => key(b) - key(a));
  }

  #syncCounts() {
    const n = {
      soon: this.soon.filter((a) => this.#passes(a)).length,
      live: this.liveCount ?? 0,
      past: this.#pastVisible().length,
    };
    for (const b of this.viewEl.children) b.querySelector('.fx-count').textContent = String(n[b.dataset.id] ?? 0);
  }

  #syncChips() {
    this.kindDD?.setValue(this.kind);
    this.gradeDD?.setValue(this.nota);
  }

  async #load() {
    try {
      const data = await (await fetch('/api/formations')).json();
      this.items = data.items || [];
      this.soon = data.approaching || [];
      this.inPlay = data.in_play || [];
      this.snap = data;
      this.#syncHistCheck(data.history_check);
    } catch {
      this.items = [];
    }
    this.#anunta();
    this.#anuntaSoon();
    this.#render();
    this.#renderSoon();
  }

  async #loadStats() {
    try {
      [this.stats, this.model] = await Promise.all([
        fetch('/api/setups/stats?days=30').then((r) => r.json()),
        fetch('/api/model').then((r) => r.json()),
      ]);
    } catch {
      this.stats = null;
    }
    this.#renderStats();
  }

  /**
   * Un semnal A nou: sunet, mesaj și — dacă e pornit „Авто" — moneda deja în terminal.
   *
   * La prima încărcare nu anunțăm nimic: lista de atunci nu e „nouă", e doar ce găsești
   * când deschizi fila, iar zece bipuri deodată te-ar învăța să oprești sunetul.
   */
  #anunta() {
    const acum = Date.now() / 1000;
    for (const it of this.items) {
      if (it.grade?.grade !== 'A') continue;
      const k = `${cheia(it)}|${it.found_at}`;
      if (this.anuntate.has(k)) continue;
      this.anuntate.add(k);
      if (this.primaIncarcare || acum - it.found_at > 300) continue;
      bip();
      const sens = it.direction === 'long' ? 'LONG' : 'SHORT';
      toast(`A · ${baseTicker(it.symbol)} ${sens} · ${it.label}`, 'info');
      if (this.auto) tiger.open(it.symbol, bursaTiger(it), it.market);
    }
    this.primaIncarcare = false;
  }

  #renderStats() {
    clear(this.statsEl);
    const s = this.stats;
    if (!s) return;
    const rand = (nota) => {
      const t = s.by_grade?.[nota];
      if (!t?.n) return el('span', { class: 'form-stat mute', text: `${nota}: ещё нет сделок` });
      return el('span', { class: `form-stat ${t.avg_r > 0 ? 'up' : 'down'}` }, [
        el('b', { text: nota }),
        ` ${t.n} сд. · ${Math.round(t.winrate * 100)}% в плюс · `,
        el('b', { text: `${semn(t.avg_r, 2)}R` }),
        ' на сделку',
      ]);
    };
    this.statsEl.append(
      el('span', {
        class: 'form-stat-k',
        title: 'Каждый сигнал проверяется по минутным свечам: вход — открытие следующей минуты, '
          + 'стоп за уровнем, половина на цели 1 (+1.5% или 2R, что ближе) со стопом в безубыток, '
          + 'остаток на цели 2, через час — выход по рынку. Комиссия Bybit 0.11% '
          + 'за круг вычтена. Если стоп и цель задеты одной свечой — считается стоп.',
        text: `Журнал ${s.days} дн. (после комиссии)`,
      }),
      rand('A'), rand('B'), rand('C'),
      s.open ? el('span', { class: 'form-stat mute', text: `в работе: ${s.open}` }) : null,
    );
    const m = this.model;
    if (m) {
      const cand = m.fitted_at ? new Date(m.fitted_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
      this.statsEl.append(el('span', {
        class: 'form-stat mute form-model',
        title: 'Оценка A/B/C — это ожидаемый результат в R после комиссии. Модель учится на повторе '
          + 'истории (раз в сутки) и на живом журнале (каждые 30 минут); свежие сигналы весят больше.',
        text: m.n_hist + m.n_live
          ? `Модель: ${m.n_hist} истор. + ${m.n_live} живых сигналов · обновлена ${cand}`
          : 'Модель: история ещё загружается — оценки пока по гипотезам',
      }));
    }
  }

  #render() {
    if (!this.listEl) return;
    const s = this.snap || {};
    // Bursa scanată se scrie explicit: scanerul e unul pentru toată platforma, iar
    // cine lucrează pe altă bursă trebuie să ştie că monedele de aici nu sunt ale lui.
    const bursa = s.exchange ? `${s.exchange} ${s.market === 'spot' ? 'спот' : 'фьючерсы'} · ` : '';
    const flux = s.flow?.symbols ? ` · поток 4 бирж: ${s.flow.symbols} монет` : '';
    this.metaEl.textContent = s.ts
      ? `${bursa}${s.scanned} монет · ${s.tf} · обновлено ${candFmt(s.ts)}${flux}`
      : 'Первый проход ещё идёт…';

    // Tranzacțiile pornite din „Скоро" stau primele: ordinul e deja umplut, contează acum.
    // Tranzacțiile din „Скоро" nu au fel de formație: când e ales un fel anume, nu intră.
    const dinApropieri = this.kind ? [] : (this.inPlay || []).map((r) => this.#itemFromPlay(r)).filter((i) => this.#passes(i));
    const vizibile = [...dinApropieri, ...this.#sorted(this.items
      .filter((i) => (!this.kind || i.kind === this.kind) && i.score >= this.minScore)
      .filter((i) => !this.nota || this.nota.includes(i.grade?.grade || 'C'))
      .filter((i) => this.#passes(i)))];

    // Diferență, nu reconstrucție: cardurile rămase își păstrează graficul viu.
    const dorite = new Map(vizibile.map((i) => [cheia(i), i]));
    for (const [k, card] of this.cards) {
      if (!dorite.has(k)) { this.#destroyCard(card); this.cards.delete(k); }
    }
    for (const [k, item] of dorite) {
      const existent = this.cards.get(k);
      if (existent) existent.refresh(item);
      else this.cards.set(k, this.#createCard(item));
    }
    for (const k of dorite.keys()) this.listEl.append(this.cards.get(k).element);
    this.liveCount = vizibile.length;
    this.#syncCounts();
    if (this.view === 'past') this.#renderPast();

    this.#renderEmpty(vizibile.length, s);
  }

  #renderEmpty(cate, s) {
    if (cate) { this.emptyEl.remove(); return; }
    clear(this.emptyEl);
    this.emptyEl.append(
      el('div', { text: s.ts ? 'Ничего не нашлось.' : 'Сканер запускается…' }),
      el('div', {
        class: 'ideas-empty-sub',
        text: s.ts
          ? 'Это нормально: хорошие сетапы появляются не каждую минуту. '
            + 'Покажите все оценки или опустите порог, чтобы увидеть более слабые.'
          : 'Первый проход занимает около полуминуты.',
      }),
    );
    this.listEl.append(this.emptyEl);
  }

  #createCard(item) {
    const scoreEl = el('span', { class: 'form-score-badge' });
    const gradeEl = el('span', { class: 'form-grade' });
    const expEl = el('span', { class: 'form-exp' });
    const dirEl = el('span', { class: 'idea-dir' });
    const labelEl = el('span', { class: 'form-label' });
    const whenEl = el('span', { class: 'form-when' });
    const nivelEl = el('span', { class: 'form-num-v' });
    const pretEl = el('span', { class: 'form-num-v' });
    const distEl = el('span', { class: 'form-num-v' });
    const volEl = el('span', { class: 'form-num-v' });
    const potEl = el('span', { class: 'form-num-v' });
    const planEl = el('div', { class: 'form-plan' });
    const fluxEl = el('div', { class: 'form-flow' });
    const motiveEl = el('div', { class: 'form-reasons' });
    const whyEl = el('div', { class: 'form-why' });
    const chartEl = el('div', { class: 'form-chart' });
    const tigerSlot = el('span', { class: 'form-tiger' });
    const nobySlot = el('span', { class: 'form-noby' });

    const num = (k, v) => el('div', { class: 'form-num' }, [
      el('span', { class: 'form-num-k', text: k }), v,
    ]);

    // Antetul are două rânduri fixe: butoanele pe primul (nu se rup niciodată), textul pe
    // al doilea (tăiat cu „…" dacă nu încape) — înainte totul curgea și se rupea aleator.
    const element = el('article', { class: 'form-card' }, [
      el('div', { class: 'form-top' }, [
        gradeEl,
        expEl,
        scoreEl,
        ...coinButtons(item, () => this.app.openInstrument(card.item)),
        tigerSlot,
        dirEl,
        el('span', { class: 'form-spacer' }),
        el('button', {
          class: 'form-expand',
          title: 'Большой график: плотности, крупные сделки, поглощение',
          onclick: () => this.#open(card),
        }, [icon('expand', 12)]),
      ]),
      el('div', { class: 'form-sub' }, [labelEl, nobySlot, whenEl]),
      // Cât se mişcă moneda într-o lumânare, în procente. Fără cifra asta, două
      // formaţii cu acelaşi punctaj arată la fel, deşi din una se poate lua un procent
      // şi din cealaltă nimic.
      el('div', { class: 'form-nums cinci' }, [
        num('Уровень', nivelEl), num('Цена', pretEl),
        num('До уровня', distEl), num('Ход/свеча', potEl), num('Оборот', volEl),
      ]),
      planEl,
      fluxEl,
      motiveEl,
      chartEl,
      miniLegend(item.kind),
      whyEl,
    ]);

    const card = {
      element, chartEl, chart: null, observer: null, linii: [], plan: null,
      refresh: (nou) => {
        card.item = nou;
        element.className = `form-card ${nou.kind}`;
        const g = nou.grade || {};
        // Tranzacția pornită din „Скоро" n-are notă și punctaj: în locul lor, ce este.
        const tranzactie = nou.kind === 'approach_trade';
        gradeEl.hidden = tranzactie;
        expEl.hidden = tranzactie;
        gradeEl.className = `form-grade g${g.grade || 'C'}`;
        gradeEl.textContent = g.grade || '—';
        const e = g.E;
        gradeEl.title = e === null || e === undefined ? 'Оценка сетапа'
          : `Ожидание ${e >= 0 ? '+' : ''}${e.toFixed(2)}R после комиссии — средний результат похожих сигналов`
            + (g.n ? ` (в группе ${g.n})` : '');
        expEl.className = `form-exp ${e > 0 ? 'up' : 'down'}`;
        expEl.textContent = e === null || e === undefined ? '' : `${e >= 0 ? '+' : ''}${e.toFixed(2)}R`;
        expEl.title = gradeEl.title;
        scoreEl.className = tranzactie ? 'fs-badge' : `form-score-badge s${Math.floor((nou.score ?? 0) / 10)}`;
        scoreEl.textContent = tranzactie ? 'в сделке'
          : nou.score === null || nou.score === undefined ? '—' : String(nou.score);
        scoreEl.title = tranzactie ? 'Ордер из «Скоро» исполнен — сделка идёт' : 'Баллы формации по графику';
        const lung = nou.direction === 'long';
        dirEl.className = `idea-dir ${lung ? 'long' : 'short'}`;
        dirEl.textContent = lung ? 'LONG' : 'SHORT';
        labelEl.textContent = nou.label;
        whenEl.textContent = candFmt(nou.found_at);
        nivelEl.textContent = fmtPrice(nou.level);
        pretEl.textContent = fmtPrice(nou.price);
        distEl.className = `form-num-v ${nou.distance_pct >= 0 ? 'up' : 'down'}`;
        distEl.textContent = `${nou.distance_pct >= 0 ? '+' : ''}${nou.distance_pct}%`;
        volEl.textContent = fmtVolume(nou.quoteVolume);
        const hod = nou.detail?.natr ?? nou.natr ?? 0;
        potEl.className = `form-num-v ${hod >= 1.4 ? 'up' : ''}`;
        potEl.textContent = hod ? `${Number(hod).toFixed(2)}%` : '—';

        // Butonul Tiger deschide moneda pe bursa contului; dacă Bybit n-o are, o spunem.
        clear(tigerSlot);
        clear(nobySlot);
        const unde = bursaTiger(nou);
        const btn = tigerBtn(nou.symbol, unde, nou.market);
        if (btn) {
          btn.title = `Открыть ${nou.symbol} в Tiger.Trade (${unde})`;
          tigerSlot.append(btn);
        }
        if (nou.bybit === false) {
          nobySlot.append(el('span', { class: 'form-nobybit', text: 'нет на Bybit' }));
        }

        renderPlan(planEl, nou);
        renderFlow(fluxEl, nou.flow, nou.direction);
        clear(motiveEl);
        if (nou.coin24?.n || nou.coin7?.n) {
          motiveEl.append(el('span', {
            class: 'form-reason base',
            title: 'Как отработали сделки этой монеты (формации и оба плана подходов) за 24 часа и 7 дней, '
              + 'после комиссии. Хранится вся история.',
            text: coinText(nou.coin24, nou.coin7),
          }));
        }
        const baza = g.parts?.[0];
        if (baza) {
          motiveEl.append(el('span', {
            class: 'form-reason base',
            title: 'Средний результат после комиссии у сигналов того же типа, баллов и комиссии — '
              + 'по истории последних недель и живому журналу, свежие весят больше',
            text: `${baza.name}: ${baza.r >= 0 ? '+' : ''}${baza.r.toFixed(2)}R (${baza.n} сигн.)`,
          }));
        }
        for (const p of g.pro || []) motiveEl.append(el('span', { class: 'form-reason pro', text: `+ ${p}` }));
        for (const c of g.contra || []) motiveEl.append(el('span', { class: 'form-reason contra', text: `− ${c}` }));

        clear(whyEl);
        for (const [k, v] of Object.entries(nou.detail || {})) {
          const e = EXPLICATII[k];
          if (!e) continue;
          whyEl.append(el('span', { class: 'form-why-chip' }, [
            el('span', { class: 'form-why-k', text: e[0] }),
            el('span', { class: 'form-why-v', text: e[1](v) }),
          ]));
        }
        // Nivelul și planul se pot rafina de la un tur la altul — mutăm liniile, nu graficul.
        const semnatura = `${nou.level}|${nou.plan?.entry}|${nou.plan?.stop}`;
        if (semnatura !== card.plan) { card.plan = semnatura; this.#drawLevels(card); }
      },
    };
    card.refresh(item);
    // Clic pe graficul mic = același lucru ca butonul: graficul mare cu tot ce se vede.
    chartEl.addEventListener('click', () => this.#open(card));
    chartEl.title = 'Открыть большой график';

    // Graficul se naște când cardul intră în ecran. Containerul cu scroll e lista, nu
    // fereastra — altfel toate cardurile par vizibile din prima clipă.
    card.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || card.chart) continue;
        card.chart = new Chart(chartEl, {
          exchange: this.#chartEx(card.item),
          market: item.market,
          symbol: item.symbol,
          tf: item.tf || this.snap?.tf || '5m',
          compact: true,
          limit: 200,
          indicators: { volume: true },
          showLevels: false,   // singurele niveluri care contează aici sunt ale planului
        });
        card.chart.load()
          .then(() => this.#drawLevels(card))
          .catch((err) => {
            chartEl.append(el('div', { class: 'card-error', text: 'Не удалось загрузить свечи' }));
            console.warn(`[formations] ${item.symbol}: ${err.message}`);
          });
        card.observer.disconnect();
      }
    }, { root: this.listEl, rootMargin: '250px' });
    card.observer.observe(element);

    return card;
  }

  /** Vederea mare a unui setup; primește mereu ultima versiune a formației. */
  #open(card) {
    const it = card.item;
    openSetupView(it, {
      app: this.app,
      chartExchange: this.#chartEx(it),
      tigerExchange: bursaTiger(it),
      latest: () => this.items.find((i) => cheia(i) === cheia(it)) || it,
      // Cât timp formația e în listă: nimic. După ce iese: soarta ei din jurnal.
      history: () => {
        if (this.items.some((i) => cheia(i) === cheia(it))) return null;
        const r = this.past.find((x) => x.symbol === it.symbol && x.kind === it.kind
          && x.direction === it.direction && x.found_at === it.found_at);
        if (!r) return { status: 'gone', text: 'Формация ушла из списка: событие прошло. Это не значит, что сетап сломан — смотрите итог во вкладке «Прошедшие».' };
        const st = { win: 'обе цели достигнуты', partial: 'половина на цели 1, остаток в безубыток',
          loss: 'выбит стоп', timeout: 'закрыт по времени',
          missed: 'пробоя не было', skip: 'вход пропущен', open: 'сделка по плану ещё идёт' }[r.status] || r.status;
        const rez = r.net_r !== null && r.net_r !== undefined ? ` (${r.net_r >= 0 ? '+' : ''}${r.net_r.toFixed(2)}R)`
          : r.cur_r !== null && r.cur_r !== undefined ? ` (сейчас ${r.cur_r >= 0 ? '+' : ''}${r.cur_r.toFixed(2)}R)` : '';
        return { status: r.status, text: `Формация ушла из списка (событие прошло) — ${st}${rez}` };
      },
    });
  }

  /** Nivelul formației și planul, trasate peste lumânări. */
  #drawLevels(card) {
    card.linii = drawPlanLines(card.chart, card.item, chartTheme().levelLine, card.linii);
  }

  #destroyCard(card) {
    card.observer?.disconnect();
    card.chart?.destroy();
    card.element.remove();
  }

  unmount() {
    this.#closeFilters();
    clearInterval(this.timer);
    clearInterval(this.statsTimer);
    clearInterval(this.pastTimer);
    this.soonAnuntate.clear();
    for (const card of this.soonCards?.values() || []) this.#destroyCard(card);
    this.soonCards?.clear();
    for (const card of this.cards.values()) this.#destroyCard(card);
    this.cards.clear();
    this.root?.remove();
  }
}
